import {
  AssociatedVehicle,
  AssociatedVehicleTimelineEvents,
  AssociatedVehicleTimelineRequest,
  CountRequest,
  CountResponse,
  ListEventsRequest,
  VehicleAgeTimeline,
} from "shared/api/api";
import { MatchClaimsResponse } from "shared/api/claims/api";
import { useAPI, useLoadMorePages, usePaginatedAPI } from "shared/api/hooks";
import { CountRequestWithVehiclesFilter } from "shared/api/vehicles/api";

import {
  AssociatedSignalEvent,
  getSignalEventsByVehicleAgeTimelineRequestURI,
  getSignalEventsOccurrencesRunChartRequestURI,
  getSignalEventsTopContributorsRequestURI,
  listAssociatedVehiclesTimelineRequestURI,
  ListSignalEventAssociatedSignalEventsRequest,
  listSignalEventAssociatedSignalEventsRequestURI,
  listSignalEventOccurrencesVINAggregateCountRequestURI,
  ListSignalEventOccurrencesVINAggregateRequest,
  listSignalEventOccurrencesVINAggregateRequestURI,
  ListSignalEventOccurrencesVINTimelineRequest,
  listSignalEventOccurrencesVINTimelineRequestURI,
  listSignalEventsAssociatedClaimsRequestURI,
  listSignalEventsAssociatedRepairsRequestURI,
  listSignalEventsAssociatedVehiclesCountRequestURI,
  listSignalEventsAssociatedVehiclesRequestURI,
  listSignalEventsOccurrencesCountRequestURI,
  listSignalEventsOccurrencesRequestURI,
  ListSignalEventsRequest,
  listSignalEventsRequestURI,
  ListSignalEventsSmartGroupsRequest,
  listSignalEventsSmartGroupsRequestURI,
  MatchSignalEventClaimsRequest,
  matchSignalEventClaimsRequestURI,
  SignalEvent,
  SignalEventAssociatedClaim,
  SignalEventAssociatedRepair,
  SignalEventByVehicleAgeFiltersRequest,
  SignalEventOccurrencesRunChartResponse,
  SignalEventOccurrencesVINAggregateBucket,
  SignalEventOccurrencesVINTimelineBucket,
  SignalEventsAssociatedClaimsRequest,
  SignalEventsAssociatedRepairsRequest,
  SignalEventsAssociatedVehiclesRequest,
  SignalEventSmartGroup,
  SignalEventsOccurrence,
  SignalEventsRunChartRequest,
  SignalEventsTopContributor,
  SignalEventsTopContributorsRequest,
} from "./api";

export const useListSignalEvents = (args: ListSignalEventsRequest) =>
  usePaginatedAPI<typeof args, SignalEvent[]>(listSignalEventsRequestURI, args);

export const useListSignalEventsOccurrences = (args: ListEventsRequest) =>
  usePaginatedAPI<typeof args, SignalEventsOccurrence[]>(
    listSignalEventsOccurrencesRequestURI,
    args
  );

export const useListSignalEventsOccurrencesCount = (
  args: CountRequestWithVehiclesFilter
) =>
  usePaginatedAPI<typeof args, CountResponse>(
    listSignalEventsOccurrencesCountRequestURI,
    args
  );

export const useSignalEventsOccurrencesRunChart = (
  args: SignalEventsRunChartRequest
) =>
  useAPI<typeof args, SignalEventOccurrencesRunChartResponse>(
    getSignalEventsOccurrencesRunChartRequestURI,
    args
  );

export const useListSignalEventsAssociatedClaims = (
  args: SignalEventsAssociatedClaimsRequest
) =>
  usePaginatedAPI<typeof args, SignalEventAssociatedClaim[]>(
    listSignalEventsAssociatedClaimsRequestURI,
    args
  );

export const useListSignalEventsAssociatedRepairs = (
  args: SignalEventsAssociatedRepairsRequest
) =>
  usePaginatedAPI<typeof args, SignalEventAssociatedRepair[]>(
    listSignalEventsAssociatedRepairsRequestURI,
    args
  );

export const useListSignalEventsByVehicleAgeTimeline = (
  args: SignalEventByVehicleAgeFiltersRequest
) =>
  useAPI<typeof args, VehicleAgeTimeline[]>(
    getSignalEventsByVehicleAgeTimelineRequestURI,
    args
  );

export const useListSignalEventsTopContributors = (
  args: SignalEventsTopContributorsRequest
) =>
  usePaginatedAPI<typeof args, SignalEventsTopContributor[]>(
    getSignalEventsTopContributorsRequestURI,
    args
  );

export const useSignalEventsAssociatedVehicles = (
  args: SignalEventsAssociatedVehiclesRequest
) =>
  usePaginatedAPI<typeof args, AssociatedVehicle[]>(
    listSignalEventsAssociatedVehiclesRequestURI,
    args
  );

interface SignalEventsAssociatedVehiclesCountRequest extends CountRequest {
  signalEventOccurrencesFilter?: string;
}

export const useSignalEventsAssociatedVehiclesCount = (
  args: SignalEventsAssociatedVehiclesCountRequest
) =>
  useAPI<typeof args, CountResponse>(
    listSignalEventsAssociatedVehiclesCountRequestURI,
    args
  );

export const useListSignalEventOccurrencesVINTimeline = (
  args: ListSignalEventOccurrencesVINTimelineRequest
) =>
  usePaginatedAPI<typeof args, SignalEventOccurrencesVINTimelineBucket[]>(
    listSignalEventOccurrencesVINTimelineRequestURI,
    args
  );

export const useListSignalEventOccurrencesVINAggregate = (
  args: ListSignalEventOccurrencesVINAggregateRequest
) =>
  usePaginatedAPI<typeof args, SignalEventOccurrencesVINAggregateBucket[]>(
    listSignalEventOccurrencesVINAggregateRequestURI,
    args
  );

export const useListSignalEventOccurrencesVINAggregateCount = (
  args: CountRequest
) =>
  usePaginatedAPI<typeof args, CountResponse>(
    listSignalEventOccurrencesVINAggregateCountRequestURI,
    args
  );

export const useListSignalEventAssociatedSignalEvents = (
  args: ListSignalEventAssociatedSignalEventsRequest
) =>
  usePaginatedAPI<typeof args, AssociatedSignalEvent[]>(
    listSignalEventAssociatedSignalEventsRequestURI,
    args
  );

export const useListSignalEventsAssociatedVehiclesTimeline = (
  args: AssociatedVehicleTimelineRequest
) =>
  useAPI<typeof args, AssociatedVehicleTimelineEvents[]>(
    listAssociatedVehiclesTimelineRequestURI,
    args
  );

export const useListSignalEventsSmartGroups = (
  args: ListSignalEventsSmartGroupsRequest,
  initialPageSize: number,
  loadMorePageSize: number
) =>
  useLoadMorePages<typeof args, SignalEventSmartGroup>(
    listSignalEventsSmartGroupsRequestURI,
    args,
    initialPageSize,
    loadMorePageSize
  );

export const useMatchedClaims = (args: MatchSignalEventClaimsRequest) =>
  useAPI<typeof args, MatchClaimsResponse>(
    () => matchSignalEventClaimsRequestURI(args),
    args
  );
